import { useAuthReady } from './useAuthReady';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  fetchFeeQuote,
  type CardPaymentContext,
  type Chain,
  type Token,
} from '../lib/transfers';

/**
 * Estimated transfer fee (USD ≈ token) for a chain/token. For Tron, pass
 * recipient + sender so the quote matches create.
 *
 * Deliberately NOT keyed on the amount for ordinary sends: TRC-20 energy does
 * not depend on the value transferred. Card top-ups re-derive locally from
 * `cardFee`. Swap deposits ARE keyed on `swapFromAmount` because partner-margin
 * absorb depends on size.
 *
 * `keepPreviousData` keeps a recipient/source change from blanking the numbers
 * mid-edit — it is a DISPLAY smoother only. It also means the numbers can belong
 * to the previous inputs, so submission must be gated on `fresh`, never on
 * whether a number happens to be present.
 */
export function useFeeQuote(
  chain: Chain,
  token: Token,
  to?: string,
  from?: string,
  cardPayment?: CardPaymentContext,
  swapFromAmount?: number,
) {
  const { canFetch: ready, initData } = useAuthReady();
  const swapAmtKey =
    swapFromAmount != null && swapFromAmount > 0 ? Math.round(swapFromAmount * 100) / 100 : '';

  const q = useQuery({
    queryKey: [
      'feeQuote',
      chain,
      token,
      to ?? '',
      from ?? '',
      cardPayment?.kind ?? '',
      cardPayment?.kind === 'issue' ? cardPayment.templateId : (cardPayment?.cardId ?? ''),
      cardPayment?.kind === 'issue' ? (cardPayment.promoCode ?? '') : '',
      swapAmtKey,
    ],
    enabled: ready,
    queryFn: () =>
      fetchFeeQuote(
        initData,
        chain,
        token,
        to,
        from,
        cardPayment,
        swapFromAmount != null && swapFromAmount > 0 ? swapFromAmount : undefined,
      ),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  return {
    feeUsd: q.data?.feeUsd,
    /** Balance to keep free for the fee — ≥ feeUsd. Gate the balance check on this. */
    maxFeeUsd: q.data ? (q.data.maxFeeUsd ?? q.data.feeUsd) : undefined,
    /** Natives: the network fee in the coin itself (decimal number), else null. */
    feeNative: q.data?.native ? Number(q.data.native.amount) : null,
    /** Card payments: the fee policy behind this quote (discount rate, thresholds). */
    cardFee: q.data?.cardFee,
    /** Swap: network fee waived when partner margin covers 1-leg cost.
     *  Never true while showing placeholder data from a previous amount. */
    swapNetworkFree: !!q.data?.swapNetworkFree && !q.isPlaceholderData,
    /**
     * True only when the quote above was priced for THESE chain/token/to/from.
     * Callers must gate submission on it: while it is false the numbers are
     * either absent or carried over from the previous recipient/source, and a
     * balance check against them can offer an amount the send then rejects.
     */
    fresh: q.data != null && !q.isPlaceholderData,
    loading: q.isLoading,
  };
}
